import React, { useEffect, useState } from 'react'
import ToastMessage from '../../../../common/ToastMessage';
import CustomDataGrid from '../../../../common/CustomDataGrid';
import useVendors from '../../../../store/ReactQueries/procurements/vendors/useVendors';
import useAddVendors from '../../../../store/ReactQueries/procurements/vendors/useAddVendor';
import useUpdateVendors from '../../../../store/ReactQueries/procurements/vendors/useUpdateVendor';
import useDeleteVendor from '../../../../store/ReactQueries/procurements/vendors/useDeleteVendor';
import { GridColDef } from '@mui/x-data-grid';
import { PAGESIZE } from '../../../../utils/constants';
import { FieldErrors } from '../../../../domain/helpers/Errors';
import { validateSchema } from '../../../../common/ValidateSchema';
import { useLanguageStore } from '../../../../store/useLanguageStore';
import { VendorInterface } from '../../../../models/ProcurementModule/Vendor';
import { vendorSchema } from '../../../../domain/ValidationSchemas/PurchaseOrder/vendor';

export const Vendors = () => {
    const { language } = useLanguageStore();

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(PAGESIZE);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [form, setForm] = useState<VendorInterface>({
        id: '',
        name: '',
        address: '',
        contact: '',
        notes: '',
        businessNo: '',
        taxId: '',
        vendorStatusId: '',
        vendorStatus: { title: '' }
    });

    const [modal, setModal] = useState<{ add: boolean, edit: boolean, view: boolean }>({
        add: false,
        edit: false,
        view: false
    });

    let { data: vendors, error: vendorError, isLoading } = useVendors(page, pageSize);

    const addVendor = useAddVendors(page, pageSize);
    const updateVendor = useUpdateVendors(page, pageSize);
    const deleteVendor = useDeleteVendor(page, pageSize);

    useEffect(() => {
        if (vendorError) ToastMessage.Failed(`Error: ${vendorError.message}`);
    }, [vendorError])

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Id', type: "string", editable: false, width: 150 },
        { field: 'name', headerName: 'Name', type: "string", editable: false, width: 150 },
        { field: 'address', headerName: 'Address', type: "string", editable: false, width: 200 },
        { field: 'contact', headerName: 'Contact', type: "string", editable: false, width: 130 },
        { field: 'notes', headerName: 'Notes', type: "custom", editable: false, width: 150 },
        { field: 'businessNo', headerName: 'Business No', type: "string", editable: false },
        { field: 'taxId', headerName: 'Tax id', type: "string", editable: false },
        {
            field: "vendorStatusId",
            headerName: "VendorStatusId",
            type: "custom",
            editable: false,
        },
        {
            field: "vendorStatus",
            headerName: "VendorStatus",
            type: "string",
            editable: false,
            width: 150,
            renderCell: ({ row }) => (
                <span>
                    {row.vendorStatus
                        ? `${row.vendorStatus?.title ?? ""}`
                        : ""}
                </span>
            ),
        },
    ];

    const addItems = () => {
        setForm({
            ...form,
            id: '',
            name: '',
            address: '',
            contact: '',
            notes: '',
            businessNo: '',
            taxId: '',
            vendorStatusId: '',
            vendorStatus: { title: '' }
        });
        setModal({ ...modal, add: true });
        setErrors({});
    }

    const handleError = (error: unknown) => {
        if (error instanceof Error) ToastMessage.Failed(error.message);
        else ToastMessage.Failed();
    };

    const handleSave = async () => {
        const isValid = validateSchema(vendorSchema, form);

        setErrors(isValid.errors);
        if (!isValid.result) return;

        const { id, name, address, contact, notes, businessNo, taxId, vendorStatusId } = form;

        const upForm: VendorInterface = {
            id,
            name,
            notes,
            taxId,
            contact,
            address,
            businessNo,
            vendorStatusId
        };

        if (upForm.id === '') {
            const { id, ...formData } = upForm;
            addVendor.mutate(formData, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        } else {
            updateVendor.mutate(upForm, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        }
        setModal({ ...modal, add: false, edit: false, view: false });
    }

    const handleDelete = async (id: string) => {
        if (await ToastMessage.Confirm(language['ToastDeleteItem'])) {
            deleteVendor.mutate(id, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        }
    };

    return (
        <CustomDataGrid
            name={language['Vendors']}
            rows={vendors || []}
            form={form}
            page={page}
            modal={modal}
            errors={errors}
            columns={columns}
            loading={isLoading}
            pageSize={pageSize}
            setForm={setForm}
            setPage={setPage}
            addItems={addItems}
            setModal={setModal}
            setErrors={setErrors}
            handleSave={handleSave}
            setPageSize={setPageSize}
            handleDelete={handleDelete}
            hideColumns={['id', 'vendorStatusId',]}
            showColumnModal={['name', 'address', 'contact', 'businessNo', 'taxId', 'vendorStatusId', 'notes',]}
        />
    );
}
export default Vendors;